import React from "react";
import { Link } from "react-router-dom";
import { BsChevronRight } from "react-icons/bs";

import { cn } from "../../ui";
import type { MenuGroup, Role } from "./types";

type Props = {
  group: MenuGroup;
  role: Role;
  open: boolean;
  onToggle: () => void;
  onNavigate: () => void;
};

const sectionIdFor = (title: string) =>
  `sidebar-section-${title.toLowerCase().replace(/\s+/g, "-")}`;

const SidebarSection: React.FC<Props> = ({
  group,
  role,
  open,
  onToggle,
  onNavigate,
}) => {
  const { title, icon, menu } = group;
  const sectionId = sectionIdFor(title);
  const visibleItems = menu.filter(
    (item) => !item.roles || item.roles.includes(role),
  );

  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-controls={sectionId}
        aria-expanded={open}
        className={cn(
          "group relative flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left transition-colors",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/25",
          open ? "bg-gray-50 text-gray-900" : "text-gray-700 hover:bg-gray-50",
        )}
      >
        {open ? (
          <span
            aria-hidden
            className="absolute left-0 top-1/2 h-5 w-[2px] -translate-y-1/2 rounded-r bg-brand"
          />
        ) : null}
        <span
          className={cn(
            "shrink-0 transition-colors",
            open
              ? "text-brand"
              : "text-gray-400 group-hover:text-gray-600",
          )}
        >
          {icon}
        </span>
        <span
          className={cn(
            "flex-1 truncate text-[13px] tracking-tight",
            open ? "font-semibold" : "font-medium",
          )}
        >
          {title}
        </span>
        <BsChevronRight
          className={cn(
            "h-3 w-3 shrink-0 transition-transform duration-200",
            open ? "rotate-90 text-brand" : "text-gray-400",
          )}
        />
      </button>

      <div
        id={sectionId}
        className={cn(
          "grid overflow-hidden transition-[grid-template-rows] duration-300 ease-out",
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
      >
        <div className="min-h-0">
          <ul className="ml-[22px] mt-0.5 mb-1.5 flex flex-col border-l border-gray-200 pl-2">
            {visibleItems.map((item) => (
              <li key={item.ref}>
                <Link
                  to={item.ref}
                  onClick={onNavigate}
                  className="block rounded-md px-2.5 py-1.5 text-[12.5px] text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900"
                >
                  <span className="truncate">{item.title}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SidebarSection;
